import {
  SET_CHECKOUT_STEP,
  SET_DELIVERY_METHOD,
  SET_SHIPPING_DATA,
  SET_SELECTED_STORE,
  SET_PICKUP_DATE,
  SET_SELECTED_SLOT,
  SET_PAYMENT_METHOD,
  RESET_CHECKOUT,
  FETCH_MARKETS_SUCCESS,
  FETCH_SLOTS_SUCCESS,
  SET_SHIPPING_COST,
  SHIPPING_COST_LOADING,
  SHIPPING_COST_ERROR,
  CHECK_CART_LOADING,
  CHECK_CART_SUCCESS,
  CHECK_CART_FAIL,
  CONFIRM_CART_LOADING,
  CONFIRM_CART_SUCCESS,
  CONFIRM_CART_FAIL,
  SET_ECOMMERCE_CONFIG,
  SET_PAYMENT_METHODS,
} from "../type";
import axios from "axios";
import {
  getMarketsApi,
  getSlotsApi,
  getShippingCostApi,
  checkCartApi,
  confirmCartApi,
  getInfoApi,
  getPaymentMethodsApi,
} from "../../services/api.services";
import { buildGroceryExportObj } from "../../helper/groceryWrite";
import { checkoutIdParamUrl } from "../../helper/constant";

// ===== Mapping config e-commerce (azienda/get-info -> data.settings) =====

const toNumber = (v) => Number(v) || 0;

// Normalizza i settings dell'azienda in un oggetto config del checkout.
// Focus: abilitazione pickup/delivery da ecommerce_tipo_pv
// (1 = solo ritiro, 2 = solo consegna, 3 = entrambi).
// Le altre info (spese spedizione, distanza max, minimi, PV) vengono
// comunque salvate per sviluppi futuri.
export const mapEcommerceSettings = (s = {}) => {
  const typeId = s?.ecommerce_tipo_pv?.id || 3;
  return {
    ecommerceType: typeId,
    pickupEnabled: typeId === 1 || typeId === 3,
    deliveryEnabled: typeId === 2 || typeId === 3,
    // --- salvate per sviluppi futuri ---
    storeIdDefault: s?.ecommerce_id_punto_vendita_default ?? null,
    storeIdMaster: s?.ecommerce_id_punto_vendita_master ?? null,
    guestCheckout: s?.ecommerce_guest_checkout ?? false,
    maxDistanceKm: s?.ecommerce_distanza_max_km ?? null,
    orderMinimumPickup: toNumber(s?.ecommerce_order_minimum_pickup),
    orderMinimumDelivery: toNumber(s?.ecommerce_order_minimum_delivery),
    deliveryCosts: toNumber(s?.ecommerce_delivery_costs),
    deliveryCostsLimit: toNumber(s?.ecommerce_delivery_costs_limit),
    // Supplemento contrassegno (cash on delivery): si applica al pagamento in
    // contanti alla consegna. 0/assente -> nessun supplemento.
    codFee: toNumber(s?.ecommerce_cod_fee),
  };
};

// ===== Simple setters =====

export const setCheckoutStep = (step) => ({ type: SET_CHECKOUT_STEP, payload: step });
export const setDeliveryMethod = (method) => ({ type: SET_DELIVERY_METHOD, payload: method });
export const setShippingData = (data) => ({ type: SET_SHIPPING_DATA, payload: data });
export const setSelectedStore = (store) => ({ type: SET_SELECTED_STORE, payload: store });
export const setPickupDate = (date) => ({ type: SET_PICKUP_DATE, payload: date });
export const setSelectedSlot = (slot) => ({ type: SET_SELECTED_SLOT, payload: slot });
export const setPaymentMethod = (method) => ({ type: SET_PAYMENT_METHOD, payload: method });
export const resetCheckout = () => ({ type: RESET_CHECKOUT });

// ===== Async thunks =====

// Carica la config e-commerce del tenant da azienda/get-info.
// In caso di errore lascia i fallback statici del reducer (entrambi i metodi).
export const fetchEcommerceConfig = () => async (dispatch) => {
  try {
    const res = await getInfoApi();
    const settings = res?.data?.settings;
    if ((res.status === 200 || res.success) && settings) {
      dispatch({ type: SET_ECOMMERCE_CONFIG, payload: mapEcommerceSettings(settings) });
      return;
    }
  } catch {
    /* fallthrough al fallback sotto */
  }
  // Fallback (get-info assente/errore): marca comunque "loaded" coi default e NON
  // blocca il guest (fail-open), cosi' la UI non resta su loader ne' forza il login
  // a sproposito quando la config non e' disponibile.
  dispatch({ type: SET_ECOMMERCE_CONFIG, payload: { guestCheckout: true } });
};

// Ri-scrive la lista (grocery-list/write) col carrello CORRENTE e ritorna il
// nuovo id_param_url, salvandolo in localStorage. Chiamata al "Procedi" dello
// step 1 per garantire che al pagamento l'id rispecchi eventuali modifiche.
export const writeGroceryList = () => async (dispatch, getState) => {
  const state = getState();
  const { exportObj, idParam } = buildGroceryExportObj(
    state.groceryList,
    state.authJWT && state.authJWT.user
  );
  await axios.post(
    `${process.env.REACT_APP_ENDPOINT}/grocery-list/write`,
    exportObj
  );
  localStorage.setItem(checkoutIdParamUrl, idParam);
  return idParam;
};

// Carica i metodi di pagamento dinamici del tenant. In caso di errore lascia i
// default statici del reducer (contanti + carta/stripe) -> il checkout resta usabile.
export const fetchPaymentMethods = () => async (dispatch) => {
  try {
    const res = await getPaymentMethodsApi();
    const methods = res?.data?.methods;
    if ((res?.success || res?.status === 200) && Array.isArray(methods) && methods.length) {
      dispatch({ type: SET_PAYMENT_METHODS, payload: methods });
    }
  } catch {
    /* fallback: default statici nel reducer */
  }
};

export const fetchMarkets = () => async (dispatch) => {
  try {
    const res = await getMarketsApi();
    if (res.status === 200 && res.data) {
      dispatch({ type: FETCH_MARKETS_SUCCESS, payload: res.data.markets || res.data || [] });
    }
  } catch { /* ignore */ }
};

export const fetchSlots = (storeId, date) => async (dispatch) => {
  try {
    const res = await getSlotsApi(storeId, date);
    if (res.status === 200 && res.data) {
      dispatch({ type: FETCH_SLOTS_SUCCESS, payload: res.data || [] });
    }
  } catch {
    dispatch({ type: FETCH_SLOTS_SUCCESS, payload: [] });
  }
};

export const fetchShippingCost = (storeId, cap, value) => async (dispatch) => {
  if (!cap || !/^\d{5}$/.test(cap)) return;

  dispatch({ type: SHIPPING_COST_LOADING });
  try {
    const res = await getShippingCostApi(storeId, cap, value);
    if (res.status === 200 && res.data) {
      dispatch({
        type: SET_SHIPPING_COST,
        payload: {
          cost: parseFloat(res.data.shipping_cost) || 0,
          available: res.data.available !== false,
        },
      });
    } else {
      dispatch({ type: SHIPPING_COST_ERROR, payload: res.message || "Spedizione non disponibile" });
    }
  } catch (err) {
    dispatch({ type: SHIPPING_COST_ERROR, payload: "Errore calcolo spedizione" });
  }
};

export const checkCart = (data) => async (dispatch) => {
  dispatch({ type: CHECK_CART_LOADING });
  try {
    const res = await checkCartApi(data);
    if (res.status === 200 && res.data) {
      dispatch({ type: CHECK_CART_SUCCESS, payload: res.data });
    } else {
      dispatch({ type: CHECK_CART_FAIL, payload: res.message || "Errore verifica carrello" });
    }
  } catch (err) {
    dispatch({ type: CHECK_CART_FAIL, payload: "Errore verifica carrello" });
  }
};

export const saveAndConfirmCart = (cartData, navigate) => async (dispatch) => {
  dispatch({ type: CONFIRM_CART_LOADING });
  try {
    // Confirm order (il carrello e' gia' sincronizzato dal cartSyncMiddleware)
    const res = await confirmCartApi(cartData);
    if (res.status === 200 && res.data) {
      dispatch({ type: CONFIRM_CART_SUCCESS });

      // Redirect to Nexi or confirmation page
      if (res.data.payment_url) {
        window.location.href = res.data.payment_url;
      } else {
        const orderId = res.data.order_id || res.data.id;
        dispatch({ type: RESET_CHECKOUT });
        navigate(`/ordine-confermato?order_id=${orderId}`);
      }
    } else {
      dispatch({ type: CONFIRM_CART_FAIL, payload: res.message || "Errore conferma ordine" });
    }
  } catch (err) {
    dispatch({ type: CONFIRM_CART_FAIL, payload: "Errore conferma ordine" });
  }
};
